import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Observable, map, shareReplay } from 'rxjs';

export interface DeviceInfo {
  platform: string;
  model: string;
  operatingSystem: string;
  osVersion: string;
  webViewVersion: string;
  manufacturer: string;
  isVirtual: boolean;
}

export interface UIMode {
  mode: 'mobile' | 'tablet' | 'desktop';
  reason: string;
}

@Injectable({
  providedIn: 'root'
})
export class DeviceService {
  private deviceInfo: DeviceInfo | null = null;
  private uiMode: UIMode | null = null;

  // Observable breakpoints for responsive design
  public readonly isHandset$ = this.breakpointObserver
    .observe(Breakpoints.Handset)
    .pipe(map(result => result.matches), shareReplay(1));

  public readonly isTablet$ = this.breakpointObserver
    .observe(Breakpoints.Tablet)
    .pipe(map(result => result.matches), shareReplay(1));

  public readonly isWeb$ = this.breakpointObserver
    .observe(Breakpoints.Web)
    .pipe(map(result => result.matches), shareReplay(1));

  constructor(private breakpointObserver: BreakpointObserver) {
    this.initializeDeviceInfo();
  }

  /**
   * Initialize device information using Capacitor Device plugin
   */
  private async initializeDeviceInfo(): Promise<void> {
    try {
      if (Capacitor.isNativePlatform()) {
        const info = await Device.getInfo();
        this.deviceInfo = {
          platform: info.platform,
          model: info.model,
          operatingSystem: info.operatingSystem,
          osVersion: info.osVersion,
          webViewVersion: info.webViewVersion,
          manufacturer: info.manufacturer,
          isVirtual: info.isVirtual
        };
      } else {
        // Web platform - use browser detection
        this.deviceInfo = {
          platform: 'web',
          model: navigator.userAgent,
          operatingSystem: 'web',
          osVersion: navigator.userAgent,
          webViewVersion: navigator.userAgent,
          manufacturer: 'web',
          isVirtual: false
        };
      }
    } catch (error) {
      // Fallback to web detection
      this.deviceInfo = {
        platform: 'web',
        model: navigator.userAgent,
        operatingSystem: 'web',
        osVersion: navigator.userAgent,
        webViewVersion: navigator.userAgent,
        manufacturer: 'web',
        isVirtual: false
      };
    }
  }

  /**
   * Get the recommended UI mode based on device capabilities and screen size
   */
  public getUIMode(): UIMode {
    if (this.uiMode) {
      return this.uiMode;
    }

    // IMPORTANT: Check for iPad first and force desktop mode for stability
    if (this.isIPad()) {
      this.uiMode = { 
        mode: 'desktop', 
        reason: 'iPad detected - forcing desktop mode for stability' 
      };
      return this.uiMode;
    }

    // Determine UI mode based on platform and screen size
    let mode: 'mobile' | 'tablet' | 'desktop' = 'desktop';
    let reason = '';

    if (Capacitor.isNativePlatform()) {
      // Native platform (iOS/Android)
      if (this.deviceInfo?.platform === 'ios') {
        // iOS devices - use screen size to determine mode
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        const maxDimension = Math.max(screenWidth, screenHeight);
        
        if (maxDimension <= 768) {
          mode = 'mobile';
          reason = 'iOS device with small screen';
        } else if (maxDimension <= 1366) {
          mode = 'tablet';
          reason = 'iOS device with medium screen';
        } else {
          mode = 'desktop';
          reason = 'iOS device with large screen (iPad Pro)';
        }
      } else if (this.deviceInfo?.platform === 'android') {
        // Android devices
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        const maxDimension = Math.max(screenWidth, screenHeight);
        
        if (maxDimension <= 768) {
          mode = 'mobile';
          reason = 'Android device with medium screen';
        } else if (maxDimension <= 1366) {
          mode = 'tablet';
          reason = 'Android device with medium screen';
        } else {
          mode = 'desktop';
          reason = 'Android device with large screen';
        }
      }
    } else {
      // Web platform - use breakpoint observer
      const screenWidth = window.innerWidth;
      if (screenWidth <= 768) {
        mode = 'mobile';
        reason = 'Web browser with small screen';
      } else if (screenWidth <= 1024) {
        mode = 'tablet';
        reason = 'Web browser with medium screen';
      } else {
        mode = 'desktop';
        reason = 'Web browser with large screen';
      }
    }

    this.uiMode = { mode, reason };
    return this.uiMode;
  }

  /**
   * Check if device is mobile (phones only)
   */
  public isMobile(): boolean {
    return this.getUIMode().mode === 'mobile';
  }

  /**
   * Check if device is tablet
   */
  public isTablet(): boolean {
    return this.getUIMode().mode === 'tablet';
  }

  /**
   * Check if device is desktop
   */
  public isDesktop(): boolean {
    return this.getUIMode().mode === 'desktop';
  }

  /**
   * Check if device is iOS
   */
  public isIOS(): boolean {
    return this.deviceInfo?.platform === 'ios';
  }

  /**
   * Check if device is Android
   */
  public isAndroid(): boolean {
    return this.deviceInfo?.platform === 'android';
  }

  /**
   * Check if device is web
   */
  public isWeb(): boolean {
    return this.deviceInfo?.platform === 'web';
  }

  /**
   * Check if device is iPad specifically
   */
  public isIPad(): boolean {
    // Check for native iOS platform first
    if (this.deviceInfo?.platform === 'ios') {
      return true; // If Capacitor detects iOS, it's definitely an iOS device
    }
    
    // Check for iPad in web mode (when using "Request Desktop Website")
    const userAgent = navigator.userAgent.toLowerCase();
    const isIPadUserAgent = userAgent.includes('ipad');
    
    // Check for iPad Pro with Macintosh user agent (desktop website mode)
    const isIPadProMac = userAgent.includes('macintosh') && 
                         this.supportsTouch() && 
                         (window.innerWidth > 768 || window.innerHeight > 768);
    
    // Check for iPad by screen dimensions and touch support
    const isIPadScreen = this.supportsTouch() && 
                        window.innerWidth > 768 && 
                        window.innerHeight > 768;
    
    return isIPadUserAgent || isIPadProMac || isIPadScreen;
  }

  /**
   * Get current device information
   */
  public getDeviceInfo(): DeviceInfo | null {
    return this.deviceInfo;
  }

  /**
   * Get current screen dimensions
   */
  public getScreenDimensions(): { width: number; height: number } {
    return {
      width: window.innerWidth,
      height: window.innerHeight
    };
  }

  /**
   * Get current orientation
   */
  public getOrientation(): 'portrait' | 'landscape' {
    return window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
  }

  /**
   * Check if device supports touch
   */
  public supportsTouch(): boolean {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  /**
   * Get pixel ratio for high-DPI displays
   */
  public getPixelRatio(): number {
    return window.devicePixelRatio || 1;
  }
}
